import { useEffect, type ReactNode } from "react";
import { useQuery } from "@tanstack/react-query";
import { Navigate, Outlet, Route, Routes, useLocation } from "react-router-dom";
import { api, readOfflineSnapshot, writeOfflineSnapshot } from "./api";
import { useI18n } from "./i18n";
import { CoursePage } from "./pages/CoursePage";
import { CoursesPage } from "./pages/CoursesPage";
import { HelpPage } from "./pages/HelpPage";
import { LoginPage } from "./pages/LoginPage";
import { OperationsPage } from "./pages/OperationsPage";
import { OutputsPage } from "./pages/OutputsPage";
import { SettingsPage } from "./pages/SettingsPage";
import { StaffSetupPage } from "./pages/StaffSetupPage";
import { StudentPortalPage } from "./pages/StudentPortalPage";
import { TemplatesPage } from "./pages/TemplatesPage";
import { TodayPage } from "./pages/TodayPage";
import { SessionProvider, useSession } from "./SessionContext";
import type { Session } from "./types";
import { AppShell } from "./ui/AppShell";
import { useOnlineStatus } from "./useOnlineStatus";

function AuthenticatedLayout(){
  const {t}=useI18n();const location=useLocation();const online=useOnlineStatus();const cached=readOfflineSnapshot<Session>("course-board-session");
  const session=useQuery({queryKey:["session"],queryFn:()=>api<Session>("/api/v1/session"),retry:false,enabled:online,initialData:cached?.value,initialDataUpdatedAt:0});
  useEffect(()=>{if(online&&session.data)writeOfflineSnapshot("course-board-session",session.data)},[online,session.data]);
  if(session.isLoading)return <div className="full-state"><div className="spinner" aria-label={t("loading")}/></div>;
  if(!online&&!session.data)return <div className="full-state"><div className="error-block">{t("offlineUnavailable")}</div></div>;
  if(!session.data)return <Navigate to="/login" state={{from:location}} replace/>;
  return <SessionProvider session={session.data}><AppShell session={session.data}><Outlet/></AppShell></SessionProvider>;
}
function RequirePermission({permission,children}:{permission:string;children:ReactNode}){return useSession().permissions.includes(permission)?children:<Navigate to="/" replace/>}

export function App(){return <Routes>
  <Route path="/login" element={<LoginPage/>}/>
  <Route path="/g/course-board/:token" element={<StudentPortalPage/>}/>
  <Route path="/staff-setup/:token" element={<StaffSetupPage/>}/>
  <Route element={<AuthenticatedLayout/>}>
    <Route index element={<TodayPage/>}/>
    <Route path="/courses" element={<CoursesPage/>}/>
    <Route path="/courses/:courseId" element={<CoursePage/>}/>
    <Route path="/tasks" element={<OperationsPage kind="tasks"/>}/>
    <Route path="/templates" element={<RequirePermission permission="course:write"><TemplatesPage/></RequirePermission>}/>
    <Route path="/outputs" element={<RequirePermission permission="export:create"><OutputsPage/></RequirePermission>}/>
    <Route path="/settings" element={<RequirePermission permission="center:configure"><SettingsPage/></RequirePermission>}/>
    <Route path="/help" element={<HelpPage/>}/>
  </Route>
  <Route path="*" element={<Navigate to="/" replace/>}/>
</Routes>}
